import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, X, Home, Shield, LogIn, ChevronRight } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';
import { motion } from 'framer-motion';
import { navItems } from '@/nav-items';
import { cn } from '@/lib/utils';

/** Routes shown to unauthenticated visitors in the top nav */
const PUBLIC_NAV_ROUTES = ['/', '/documentation', '/docs'];

/** Routes shown to authenticated users in the top nav (condensed — sidebar has the rest) */
const AUTHED_NAV_ROUTES = ['/', '/canvas', '/cloud-foundation', '/documentation'];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { session, signOut } = useAuth();
  const { isSuperUser } = useUserRole();

  const isActive = (path: string) => location.pathname === path;

  const handleSignOut = async () => {
    await signOut();
  };

  // Build the visible nav items based on auth state
  const visibleNavItems = (() => {
    if (!session) {
      // Unauthenticated: only public marketing-relevant routes
      return navItems.filter(item => PUBLIC_NAV_ROUTES.includes(item.to));
    }

    // Authenticated: condensed set — sidebar covers everything else
    return navItems.filter(item => AUTHED_NAV_ROUTES.includes(item.to));
  })();

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 bg-[var(--background)]/90 backdrop-blur-xl border-b border-[var(--border)]/50 shadow-sm"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="p-2 bg-[var(--primary)] rounded-lg">
              <Home className="h-5 w-5 text-[var(--primary-foreground)]" />
            </div>
            <div className="hidden sm:block">
              <div className="font-semibold text-[var(--foreground)]">Platform Nexus</div>
              <div className="text-xs text-[var(--muted-foreground)]">Engineering Hub</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {visibleNavItems.map(item => {
              const IconComponent = item.icon.type;

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive(item.to)
                      ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                      : 'text-[var(--muted-foreground)] hover:text-[var(--primary)] hover:bg-[var(--primary)]/5'
                  )}
                >
                  <IconComponent className="w-4 h-4" />
                  {item.title}
                </Link>
              );
            })}

            {/* Hint for authenticated users that more is in the sidebar */}
            {session && (
              <span className="flex items-center gap-1 px-3 py-2 text-xs text-[var(--muted-foreground)]">
                More in sidebar
                <ChevronRight className="w-3 h-3" />
              </span>
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {isSuperUser && (
              <Badge
                variant="secondary"
                className="hidden sm:inline-flex gap-1 bg-red-100 text-red-700 border-red-200"
              >
                <Shield className="w-3 h-3" />
                Super User
              </Badge>
            )}

            {session ? (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleSignOut}>
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button size="sm" asChild>
                  <Link to="/auth">
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </Link>
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-stone-200 py-4 bg-white/95 backdrop-blur-sm"
          >
            <nav className="flex flex-col gap-2">
              {visibleNavItems.map(item => {
                const IconComponent = item.icon.type;

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex items-center gap-3 px-3 py-3 rounded-md transition-colors',
                      isActive(item.to)
                        ? 'bg-primary/10 text-primary'
                        : 'text-stone-700 hover:bg-primary/5'
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    <IconComponent className="w-5 h-5" />
                    {item.title}
                  </Link>
                );
              })}

              <div className="mt-4 pt-4 border-t border-stone-200">
                {session ? (
                  <div className="space-y-2">
                    <p className="px-3 text-xs text-stone-400">
                      More options available in the sidebar
                    </p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        handleSignOut();
                        setIsOpen(false);
                      }}
                    >
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button className="w-full justify-start gap-2" asChild>
                      <Link to="/auth" onClick={() => setIsOpen(false)}>
                        <LogIn className="w-4 h-4" />
                        Sign In
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Navigation;
